// utils/workspaceHelpers.ts
import { API_URLS } from '../api/urls';
import { EnhancedTokenManager } from './tokenManager';

interface Workspace {
  id: number;
  name: string;
  subdomain: string;
}

interface WorkspaceApiResponse {
  workspaces: Workspace[];
}

/**
 * Ensures that onboarded users have workspace information available
 * This function should be called when a user is marked as onboarded but workspace_id is missing
 */
export const ensureWorkspaceForOnboardedUser = async (): Promise<{
  success: boolean;
  workspaceId?: number;
  error?: string;
}> => {
  try {
    const userProfile = EnhancedTokenManager.getUserProfile();
    
    // If user already has workspace info, return it
    if (userProfile?.workspace_id) {
      return {
        success: true,
        workspaceId: userProfile.workspace_id
      };
    }

    // If user is not onboarded, they shouldn't have workspace yet
    if (!userProfile?.onboarded) {
      return {
        success: false,
        error: "User is not onboarded yet"
      };
    }

    console.log("Onboarded user missing workspace info, fetching from API...");

    // Fetch workspaces from API
    const workspaceResult = await fetchUserWorkspaces();
    
    if (workspaceResult.success && workspaceResult.workspaces && workspaceResult.workspaces.length > 0) {
      const primaryWorkspace = workspaceResult.workspaces[0];
      
      // Update user profile with workspace information
      const updatedProfile = {
        ...userProfile,
        workspace_id: primaryWorkspace.id,
        workspace: primaryWorkspace
      };
      
      EnhancedTokenManager.setUserProfile(updatedProfile);
      EnhancedTokenManager.setWorkspaceDataForUser(userProfile.id, primaryWorkspace);
      
      console.log("Successfully set workspace for onboarded user:", primaryWorkspace);
      
      return {
        success: true,
        workspaceId: primaryWorkspace.id
      };
    } else {
      return {
        success: false,
        error: workspaceResult.error || "No workspaces found for onboarded user"
      };
    }
    
  } catch (error) {
    console.error("Error ensuring workspace for onboarded user:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
};

/**
 * Fetch user's workspaces from the API
 */
export const fetchUserWorkspaces = async (): Promise<{
  success: boolean;
  workspaces?: Workspace[];
  error?: string;
}> => {
  try {
    const accessToken = EnhancedTokenManager.getAccessToken();
    if (!accessToken) {
      return {
        success: false,
        error: "No access token available"
      };
    }

    console.log("Fetching user workspaces from API...");
    
    const response = await fetch(API_URLS.GET_WORKSPACES, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.error(`Failed to fetch workspaces: ${response.status}`);
      return {
        success: false,
        error: `API error: ${response.status}`
      };
    }

    const responseData: WorkspaceApiResponse = await response.json();
    console.log("User workspaces fetched successfully:", responseData);

    return {
      success: true,
      workspaces: responseData.workspaces
    };

  } catch (error) {
    console.error("Error fetching user workspaces:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error"
    };
  }
};

/**
 * Get workspace ID for current user, with automatic fetching if missing
 * This is the recommended function to use in your HomeScreen component
 */
export const getWorkspaceIdWithFallback = async (): Promise<number | null> => {
  try {
    // First try to get workspace ID from token manager
    let workspaceId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();
    
    if (workspaceId) {
      console.log("Found workspace ID in storage:", workspaceId);
      return workspaceId;
    }

    console.log("No workspace ID in storage, checking user profile...");
    
    const userProfile = EnhancedTokenManager.getUserProfile();
    
    // If user is onboarded but missing workspace, fetch it
    if (userProfile?.onboarded) {
      console.log("User is onboarded but missing workspace, fetching...");
      
      const result = await ensureWorkspaceForOnboardedUser();
      if (result.success && result.workspaceId) {
        return result.workspaceId;
      } else {
        console.warn("Could not fetch workspace for onboarded user:", result.error);
        return null;
      }
    }

    console.log("User is not onboarded or no profile found");
    return null;
    
  } catch (error) {
    console.error("Error getting workspace ID with fallback:", error);
    return null;
  }
};

/**
 * Check if user should be redirected to onboarding
 * Returns true only if user is authenticated but not onboarded
 */
export const shouldRedirectToOnboarding = (): boolean => {
  const userProfile = EnhancedTokenManager.getUserProfile();
  const isAuthenticated = !!(EnhancedTokenManager.getAccessToken() && EnhancedTokenManager.getRefreshToken());
  
  // Only redirect to onboarding if user is authenticated but not onboarded
  // Handle the null case properly
  return isAuthenticated && userProfile !== null && !userProfile.onboarded;
};

/**
 * Check if user can access dashboard
 * Returns true if user is authenticated and onboarded
 */
export const canAccessDashboard = (): boolean => {
  const userProfile = EnhancedTokenManager.getUserProfile();
  const isAuthenticated = !!(EnhancedTokenManager.getAccessToken() && EnhancedTokenManager.getRefreshToken());
  
  // Handle the null case properly
  return isAuthenticated && userProfile !== null && userProfile.onboarded;
};

/**
 * Debug function to check workspace status
 */
export const debugWorkspaceStatus = (): void => {
  const userProfile = EnhancedTokenManager.getUserProfile();
  const workspaceId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();
  const workspaceData = EnhancedTokenManager.getWorkspaceDataForCurrentUser();
  
  console.group("🏢 Workspace Status Debug");
  console.log("User Profile:", userProfile);
  console.log("Is Onboarded:", userProfile?.onboarded);
  console.log("Workspace ID from Token Manager:", workspaceId);
  console.log("Workspace Data from Token Manager:", workspaceData);
  console.log("Profile Workspace ID:", userProfile?.workspace_id);
  console.log("Profile Workspace:", userProfile?.workspace);
  console.log("Should Redirect to Onboarding:", shouldRedirectToOnboarding());
  console.log("Can Access Dashboard:", canAccessDashboard());
  console.groupEnd();
};